import React, { useCallback } from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../../state/mint/hooks'
import { Currency, ETHER, TokenAmount, WETH9, Token } from '@uniswap/sdk-core'
import { Field } from '../../../../state/mint/actions'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../../hooks'
import { usePrivy } from '@privy-io/react-auth'
import { ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { parseUnits } from 'ethers/lib/utils'
import { calculateGasMargin, getContract } from '../../../../utils'
import { Text } from 'rebass'
import { HYPERBEAT_SWAP_AND_DEPOSIT_ADDRESS, WETH9_ADDRESSES } from '@hyperswap/addresses'

import HBHYPE_DEPOSITOR_ABI from '../../../../abis/hyperbeat/swap-and-deposit.json'

import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'
import { BigNumber } from 'ethers'
import { useUSDCValue } from 'hooks/useUSDCPrice'
import { currencyId } from 'utils/currencyId'
import { useCurrency } from 'hooks/Tokens'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { Dots } from 'components/swap/styleds'
import { AutoColumn } from 'components/Column'
import { useV3SwapInfoNoMultihop } from 'state/swap/hooks'
import { V3TradeState } from 'hooks/useBestV3Trade'
import { roundToDecimalPlace } from 'utils/data'

interface StakeProps {
  key?: number
  setRefresh: () => void
  currencyIdA: string | undefined
  setCurrencyIdA: (currencyId: string | undefined) => void
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const RouteStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.text2};
  margin-top: 12px;
  .route-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .route-row-title {
    font-weight: 600;
    color: ${({ theme }) => theme.text1};
  }
  .route-row-value {
    font-weight: 400;
  }
`

/* @todo: find best route v3 or insufisiant liquidity */
const Stake: React.FC<StakeProps> = ({ setRefresh, currencyIdA, setCurrencyIdA }) => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const { login } = usePrivy()
  const [allowedSlippage] = useUserSlippageTolerance()
  const currencyA = useCurrency(currencyIdA)
  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false)
  const { dependentField, currencies, currencyBalances, parsedAmounts, noLiquidity } = useDerivedMintInfo(
    currencyA ?? undefined,
    undefined
  )
  const currencyABalance = currencyBalances[independentField]
  const { inputError, v3TradeState } = useV3SwapInfoNoMultihop({
    currencyA: currencyA ?? undefined,
    currencyB: WETH9[chainId as keyof typeof WETH9_ADDRESSES],
    typedValue: typedValue,
  })

  const noRouteFound = v3TradeState.state === V3TradeState.NO_ROUTE_FOUND

  const [approvalA, approveACallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    chainId ? HYPERBEAT_SWAP_AND_DEPOSIT_ADDRESS : undefined
  )
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)
  const fiatValueInput = useUSDCValue(parsedAmounts[Field.CURRENCY_A])
  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )
  const addTransaction = useTransactionAdder()

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          currencyIdNew === 'HYPE' ||
          (chainId !== undefined && currencyIdNew === WETH9[chainId as keyof typeof WETH9]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (currencyIdOther === 'HYPE' ||
            (chainId !== undefined && currencyIdOther === WETH9[chainId as keyof typeof WETH9]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleCurrencyASelect = (currency: Currency) => {
    const newCurrencyId = handleCurrencySelect(currency, undefined)
    setCurrencyIdA(newCurrencyId[0])
    onFieldAInput('')
  }
  async function onAdd() {
    if (!chainId || !library || !account || !currencyA) return

    const depositorContract = getContract(
      HYPERBEAT_SWAP_AND_DEPOSIT_ADDRESS,
      HBHYPE_DEPOSITOR_ABI.abi,
      library,
      account
    )

    const valueAmount = parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, currencyA?.decimals).toString()

    const isHYPE = currencyId(currencyA) === 'HYPE'

    const value = isHYPE ? valueAmount : '0'
    const valueBN = BigNumber.from(value)
    const bestFeeTier = v3TradeState.trade?.route.pools.reduce((best, pool) => {
      return Math.max(best, pool.fee)
    }, 0)

    const params = {
      tokenA:
        currencyA === ETHER ? WETH9_ADDRESSES[chainId as keyof typeof WETH9_ADDRESSES] : (currencyA as Token).address,
      amountA: valueAmount,
      fee: bestFeeTier ? bestFeeTier : 3000,
      slippage: allowedSlippage.toNumber() * 10000,
    }

    const estimate = depositorContract.estimateGas.provideAsset
    const method = depositorContract.provideAsset

    setLoadingAdd(true)
    await estimate(params, {
      ...(isHYPE ? { value: valueBN } : {}),
    })
      .then((estimatedGasLimit: any) => {
        return method(params, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
          ...(isHYPE ? { value: valueBN } : {}),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Provide ${currencyA.symbol}`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh()
          setLoadingAdd(false)
        })
      })
      .catch((error: any) => {
        setLoadingAdd(false)
        console.error(error)
      })
  }

  const isDisabled: boolean = Boolean(
    loadingAdd === true ||
      formattedAmounts[Field.CURRENCY_A].length === 0 ||
      parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
      (inputError !== undefined && inputError.length > 0) ||
      approvalA === ApprovalState.PENDING ||
      approvalA === ApprovalState.NOT_APPROVED ||
      (noRouteFound && currencyA?.symbol !== 'HYPE') ||
      (currencyA?.symbol === 'HYPE' &&
        currencyABalance &&
        parsedAmounts[Field.CURRENCY_A] &&
        currencyABalance.lessThan(parsedAmounts[Field.CURRENCY_A]))
  )

  const outputAmount =
    currencyA?.symbol !== 'HYPE'
      ? v3TradeState.trade?.outputAmount?.toSignificant()
      : parsedAmounts[Field.CURRENCY_A]?.toExact()

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id="stake-hbhype"
        onCurrencySelect={handleCurrencyASelect}
        showCommonBases
        fiatValue={fiatValueInput}
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight
            onClick={() =>
              login({
                loginMethods: ['wallet'],
                walletChainType: 'ethereum-only',
                disableSignup: false,
              })
            }
          >
            Connect Wallet
          </ButtonLight>
        ) : (
          <AutoColumn gap="8px">
            {showApprovalA && (
              <ButtonPrimary
                borderRadius="12px"
                padding={'12px'}
                onClick={approveACallback}
                disabled={approvalA === ApprovalState.PENDING}
              >
                {approvalA === ApprovalState.PENDING ? (
                  <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                ) : (
                  'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                )}
              </ButtonPrimary>
            )}

            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onAdd()}
              disabled={isDisabled}
            >
              <Text fontWeight={600} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {noRouteFound && currencyA?.symbol !== 'HYPE' ? (
                  'Insufficient Liquidity'
                ) : inputError ? (
                  inputError
                ) : loadingAdd ? (
                  <Dots>Depositing</Dots>
                ) : (
                  'Deposit'
                )}
              </Text>
            </ButtonPrimary>

            {v3TradeState.trade && currencyA?.symbol !== 'HYPE' && (
              <RouteStyled>
                <div className="route-row">
                  <div className="route-row-title">{`Route`}</div>
                  <div className="route-row-value">{`${currencyA?.symbol} > WHYPE > hbHYPE`}</div>
                </div>
                <div className="route-row">
                  <div className="route-row-title">{`Estimated Output Amount`}</div>
                  <div className="route-row-value">{`${outputAmount} hbHYPE`}</div>
                </div>
              </RouteStyled>
            )}
            {currencyA?.symbol === 'HYPE' && outputAmount && (
              <RouteStyled>
                <div className="route-row">
                  <div className="route-row-title">{`Route`}</div>
                  <div className="route-row-value">{`${currencyA?.symbol} > hbHYPE`}</div>
                </div>
                <div className="route-row">
                  <div className="route-row-title">{`Estimated Output Amount`}</div>
                  <div className="route-row-value">{`${roundToDecimalPlace(outputAmount, '2')} hbHYPE`}</div>
                </div>
              </RouteStyled>
            )}
          </AutoColumn>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Stake
